/**
 * Transaction data formatting service
 */
import { ExportData, TransactionData } from './types';
import { toTitleCase, safeString } from './utils';
import { convertDateToTimezone } from 'global-stores/timezone';
interface FormatTransactionOptions {
	numberFormatter: (value: number, format?: string) => string;
	paymentMethodMap: Record<string, string>;
	transactionCardTypes: Record<string, string>;
	headers: Array<{ key: string; label: string }>;
	timezone?: string;
}

/**
 * Transaction data formatter class
 */
export class TransactionFormatter {
	private options: FormatTransactionOptions;

	constructor(options: FormatTransactionOptions) {
		this.options = options;
	}

	/**
	 * Format a single transaction for export
	 */
	private formatTransaction = (transaction: TransactionData, timezone?: string): string[] => {
		const { numberFormatter, paymentMethodMap, transactionCardTypes } = this.options;

		return [
			// Transaction ID
			safeString(transaction.transactionId || transaction.paymentId),
			
			// Date & Time
			this.formatDateTime(transaction.time, timezone),
			
			// Amount
			this.formatAmount(numberFormatter, transaction.amount),
			
			// Type
			this.formatTransactionType(transaction, paymentMethodMap, transactionCardTypes),
			
			// Payment Method
			this.formatPaymentMethod(paymentMethodMap, transaction.card),
			
			// Status
			this.formatStatus(transaction.status),
		];
	};

	/**
	 * Format date and time using timezone-aware formatting
	 */
	private formatDateTime = (time?: string | number | Date, timezone?: string): string => {
		if (!time) return '';
		return convertDateToTimezone(time, "MMM dd, yyyy hh:mm a", timezone);
	};

	/**
	 * Format amount with currency symbol
	 */
	private formatAmount = (
		numberFormatter: (value: number, format?: string) => string,
		amount?: string | number
	): string => {
		if (!amount) return '$0.00';
		return `$${numberFormatter(Number(amount))}`;
	};

	/**
	 * Format transaction type based on business logic
	 */
	private formatTransactionType = (
		transaction: TransactionData,
		paymentMethodMap: Record<string, string>,
		transactionCardTypes: Record<string, string>
	): string => {
		if (!transaction.card) return safeString(transaction.type);

		const cardType = transaction.card as keyof typeof paymentMethodMap;
		const paymentMethodValue = paymentMethodMap[cardType];
		
		return paymentMethodValue && paymentMethodValue.includes('card')
			? transactionCardTypes.SubscriptionCard || 'Subscription-Card'
			: safeString(transaction.type);
	};

	/**
	 * Format payment method
	 */
	private formatPaymentMethod = (
		paymentMethodMap: Record<string, string>,
		card?: string
	): string => {
		if (!card) return '';
		return toTitleCase(paymentMethodMap[card] || card);
	};

	/**
	 * Format status with title case
	 */
	private formatStatus = (status?: string): string => {
		return status ? toTitleCase(status) : '';
	};

	/**
	 * Format multiple transactions for export
	 */
	public formatTransactionsForExport = (transactions: TransactionData[], timezone?: string): ExportData | null => {
		if (!transactions.length) {
			return null;
		}

		const headers = this.options.headers.map(header => header.label);
		const data = transactions.map((transaction) => this.formatTransaction(transaction, timezone));

		return { headers, data };
	};
}

/**
 * Factory function to create transaction formatter
 */
export const createTransactionFormatter = (options: FormatTransactionOptions): TransactionFormatter => {
	return new TransactionFormatter(options);
};
